/**
 * useEvidence Hook
 * Evidence fetching, upload, and finding closure operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { findingKeys } from './useFindings';
import { auditTrailKeys } from './useAuditTrail';
import type { EvidencePhoto, Finding, FindingListResponse } from '../types/api';

export const evidenceKeys = {
  all: ['evidence'] as const,
  byFinding: (findingId: string) => [...evidenceKeys.all, findingId] as const,
};

/**
 * Fetch evidence photos for a finding
 */
export function useEvidenceForFinding(findingId: string | undefined) {
  return useQuery({
    queryKey: evidenceKeys.byFinding(findingId!),
    queryFn: () => api.findings.getEvidence(findingId!),
    enabled: Boolean(findingId),
    select: (data) => data.photos,
  });
}

/**
 * Helper: optimistically update a finding's status across all cached finding queries
 */
function optimisticStatusUpdate(
  queryClient: ReturnType<typeof useQueryClient>,
  findingId: string,
  newStatus: Finding['status']
) {
  const previousData = new Map<readonly unknown[], unknown>();

  // Update any cached finding list queries
  queryClient.getQueriesData<FindingListResponse>({ queryKey: findingKeys.all }).forEach(
    ([key, data]) => {
      if (!data) return;
      previousData.set(key, data);
      if (Array.isArray(data)) {
        queryClient.setQueryData(
          key,
          (data as Finding[]).map((f) => (f.id === findingId ? { ...f, status: newStatus } : f))
        );
      } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        queryClient.setQueryData(key, {
          ...data,
          data: data.data.map((f: Finding) => (f.id === findingId ? { ...f, status: newStatus } : f)),
        });
      }
    }
  );

  return previousData;
}

/**
 * Upload evidence for a finding
 */
export function useUploadEvidence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ findingId, photoIds, source }: {
      findingId: string;
      photoIds?: string[];
      source?: string;
    }) => api.findings.uploadEvidence(findingId, { photo_ids: photoIds, source }),
    onMutate: async ({ findingId }) => {
      await queryClient.cancelQueries({ queryKey: findingKeys.all });
      return { previousData: optimisticStatusUpdate(queryClient, findingId, 'evidence_submitted') };
    },
    onError: (_, __, context) => {
      // Roll back optimistic update
      context?.previousData?.forEach((data, key) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: (_, __, { findingId }) => {
      queryClient.invalidateQueries({ queryKey: evidenceKeys.byFinding(findingId) });
      queryClient.invalidateQueries({ queryKey: findingKeys.all });
      queryClient.invalidateQueries({ queryKey: auditTrailKeys.all });
    },
  });
}

/**
 * Close a finding (transition evidence_submitted → closed)
 */
export function useCloseFinding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ findingId }: { findingId: string }) =>
      api.findings.transitionStatus(findingId, 'closed'),
    onMutate: async ({ findingId }) => {
      await queryClient.cancelQueries({ queryKey: findingKeys.all });
      return { previousData: optimisticStatusUpdate(queryClient, findingId, 'closed') };
    },
    onError: (_, __, context) => {
      context?.previousData?.forEach((data, key) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: findingKeys.all });
      queryClient.invalidateQueries({ queryKey: auditTrailKeys.all });
    },
  });
}

export default useEvidenceForFinding;
